import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/**
 * The two screens people arrive at from a link in their mail: confirming an address, and setting a
 * new password.
 *
 * They are one component because they are the same shape — a token from the URL, one call, one
 * answer — and because both have to work for someone who is not signed in and may never have been
 * on this device before.
 */
export default function TokenPage({ mode, token, onDone }) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [state, setState] = useState(mode === 'verify' ? 'working' : 'ready')
  const [error, setError] = useState(null)

  useEffect(() => {
    if (mode !== 'verify') return

    api
      .verifyEmail(token)
      .then(() => setState('done'))
      .catch((failure) => {
        setError(failure.message)
        setState('failed')
      })
  }, [mode, token])

  async function reset(event) {
    event.preventDefault()
    setState('working')
    setError(null)

    try {
      await api.resetPassword(token, password)
      setState('done')
    } catch (failure) {
      setError(failure.message)
      setState('failed')
    }
  }

  const finish = (
    <button type="button" className="button" onClick={onDone}>
      {t('token.continue')}
    </button>
  )

  if (mode === 'verify') {
    return (
      <section className="card card--center">
        <h2>{t('token.verifyTitle')}</h2>
        {state === 'working' && <p className="muted">{t('status.loading')}</p>}
        {state === 'done' && <p className="status status--good">{t('token.verified')}</p>}
        {state === 'failed' && <p className="status status--bad">{error ?? t('token.expired')}</p>}
        {state !== 'working' && finish}
      </section>
    )
  }

  return (
    <section className="card card--center">
      <h2>{t('token.resetTitle')}</h2>

      {state === 'done' ? (
        <>
          {/* Every session ended when the password changed, including any the intruder held. */}
          <p className="status status--good">{t('token.passwordChanged')}</p>
          {finish}
        </>
      ) : (
        <form onSubmit={reset}>
          <label className="field">
            {t('token.newPassword')}
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          <p className="muted small">{t('login.passwordHint', { count: 10 })}</p>

          {error && <p className="status status--bad">{error}</p>}

          <button type="submit" className="button" disabled={state === 'working' || password.length < 10}>
            {t('token.setPassword')}
          </button>
        </form>
      )}
    </section>
  )
}
